import { useEffect, useState } from "react";
import {
  DollarLineIcon,
  CheckCircleIcon,
  TimeIcon,
  AlertIcon,
} from "../../icons";
import { useApi } from "../../hooks/useApi";
import { useStateValue } from "../../context/StateValueContext";
import { hasPermission } from "../../functions/permissionUtils";
import { PERMISSIONS } from "../../constants/permissions";
import { Currency, DashboardTab } from "../../pages/Dashboard";
import PpLogo from "../common/PpLogo";
import StatCard from "../common/StatCard";
import OutcomesBreakdown from "./OutcomesBreakdown";

interface DisputeStats {
  winRate: number;
  wonDisputes: number;
  lostDisputes: number;
  recoveredAmount: number;
  underReviewDisputes: number;
  openDisputes?: number;
  refundedDisputes: number;
  refundedAmount: number;
  acceptedDisputes: number;
  resolvedDisputes: number;
  chargebacksReceived: number;
}

interface BrandDisputeStats extends DisputeStats {
  brandId: number;
  brandName: string;
}

interface AccountDisputeStats extends DisputeStats {
  accountId: string;
  accountName: string;
  accountType: string;
  needResponseInquiries?: number;
}

interface StatsResponseBase {
  hasPaypalAccount: boolean;
  hasMultipleBrands: boolean;
  hasMultiplePpAccounts: boolean;
}

interface OverallStatsResponse extends StatsResponseBase {
  currentPeriod: DisputeStats;
  previousPeriod: DisputeStats;
  needResponseInquiries?: number;
}

interface PaypalStatsResponse extends StatsResponseBase {
  paypalBankReturn?: DisputeStats;
  paypalClaim?: DisputeStats;
  paypalDispute?: DisputeStats;
  paypalUnauthorizedClaim?: DisputeStats;
  paypalCardChargeback?: DisputeStats;
  paypalPreChargebackAlert?: DisputeStats;
  paypalUnknown?: DisputeStats;
  needResponseInquiries?: number;
}

interface BrandsStatsResponse extends StatsResponseBase {
  currentPeriodByBrand?: BrandDisputeStats[];
}

interface AccountsStatsResponse extends StatsResponseBase {
  currentPeriodByAccount?: AccountDisputeStats[];
}

// Thin wrapper over the shared StatCard. Keeps the existing call-site prop shape
// (icon/iconBg were never rendered) and adds per-metric `polarity` for delta chips.
const DashboardItem = ({
  label,
  value,
  previousValue,
  badgeDeltaSymbol,
  isCurrency,
  valueSymbol,
  currency,
  tooltip,
  polarity,
  tone,
  size,
  href,
}: {
  icon?: React.ReactNode;
  iconBg?: string;
  label: string;
  value: number;
  previousValue?: number;
  badgeDeltaSymbol?: string;
  valueSymbol?: string;
  isCurrency?: boolean;
  currency?: Currency;
  tooltip?: string;
  polarity?: "positive" | "negative" | "neutral";
  tone?: "good" | "warn" | "bad";
  size?: "hero" | "default";
  href?: string;
}) => (
  <StatCard
    label={label}
    value={value}
    previousValue={previousValue}
    isCurrency={isCurrency}
    currency={currency}
    valueSymbol={valueSymbol}
    deltaSymbol={badgeDeltaSymbol}
    tooltip={tooltip}
    polarity={polarity}
    tone={tone}
    size={size}
    href={href}
  />
);

// Helper function to check if stats are not empty
const isStatsNotEmpty = (stats: DisputeStats | undefined): boolean => {
  if (!stats) return false;
  return (
    stats.resolvedDisputes > 0 ||
    stats.wonDisputes > 0 ||
    stats.lostDisputes > 0 ||
    stats.acceptedDisputes > 0 ||
    stats.recoveredAmount > 0 ||
    stats.underReviewDisputes > 0 ||
    (stats.openDisputes ?? 0) > 0
  );
};

type PaypalRowLinks = {
  flow: (cmStatuses: string, scope?: Record<string, string>) => string;
  snapshot: (cmStatuses: string, scope?: Record<string, string>) => string;
  inquiries: (scope?: Record<string, string>) => string;
  assignedScope: Record<string, string>;
};

function paypalDashboardRow(label: string, stats: DisputeStats, currency: Currency, links: PaypalRowLinks, reportingCategory: string, isInquiry: boolean = false, needResponseInquiries?: number) {
  // Scope every link to this PayPal reporting category so the list count matches the card.
  const scope = { ppTypes: "paypal", reportingCategories: reportingCategory };
  return (
    <>
      <h3 className="mt-8 mb-4 text-lg font-semibold text-gray-800 dark:text-white">
        {label}
      </h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-6 md:gap-6">
        {isInquiry && needResponseInquiries !== undefined && (
          <DashboardItem
            icon={<TimeIcon className="text-yellow-600 size-6 dark:text-yellow-400" />}
            iconBg="bg-yellow-100 dark:bg-yellow-900/30"
            label="Awaiting Response"
            value={needResponseInquiries}
            href={links.inquiries(scope)}
          />
        )}
        {!isInquiry && (
          <DashboardItem
            icon={<TimeIcon className="text-yellow-600 size-6 dark:text-yellow-400" />}
            iconBg="bg-yellow-100 dark:bg-yellow-900/30"
            label="Under Review"
            value={stats.underReviewDisputes}
            href={links.snapshot("under_review", { ...scope, ...links.assignedScope })}
          />
        )}
        <DashboardItem
          icon={<CheckCircleIcon className="text-green-600 size-6 dark:text-green-400" />}
          iconBg="bg-green-100 dark:bg-green-900/30"
          label="Won"
          value={stats.wonDisputes}
          href={links.flow("won", scope)}
        />
        {!isInquiry && (
          <DashboardItem
            icon={<AlertIcon className="text-orange-600 size-6 dark:text-orange-400" />}
            iconBg="bg-orange-100 dark:bg-orange-900/30"
            label="Lost"
            value={stats.lostDisputes}
            href={links.flow("lost", scope)}
          />
        )}
        {!isInquiry && (
          <DashboardItem
            icon={<AlertIcon className="text-orange-600 size-6 dark:text-orange-400" />}
            iconBg="bg-orange-100 dark:bg-orange-900/30"
            label="Open Disputes"
            value={stats.openDisputes ?? 0}
            href={links.snapshot("open", scope)}
          />
        )}
        {!isInquiry && (
          <DashboardItem
            icon={<CheckCircleIcon className="text-green-600 size-6 dark:text-green-400" />}
            iconBg="bg-green-100 dark:bg-green-900/30"
            label="Win Rate"
            value={stats.winRate}
            valueSymbol="%"
            badgeDeltaSymbol="%"
          />
        )}
        <DashboardItem
          icon={<DollarLineIcon className="text-blue-600 size-6 dark:text-blue-400" />}
          iconBg="bg-blue-100 dark:bg-blue-900/30"
          label="Recovered"
          value={stats.recoveredAmount}
          isCurrency={true}
          currency={currency}
          href={links.flow("won", scope)}
        />
        {isInquiry && (
          <DashboardItem
            icon={<DollarLineIcon className="text-blue-600 size-6 dark:text-blue-400" />}
            iconBg="bg-blue-100 dark:bg-blue-900/30"
            label="Refunded"
            value={stats.refundedDisputes}
            href={links.flow("refunded", scope)}
          />
        )}
        {isInquiry && (
          <DashboardItem
            icon={<DollarLineIcon className="text-blue-600 size-6 dark:text-blue-400" />}
            iconBg="bg-blue-100 dark:bg-blue-900/30"
            label="Refunded Amount"
            value={stats.refundedAmount}
            isCurrency={true}
            currency={currency}
            href={links.flow("refunded", scope)}
          />
        )}
      </div>
    </>
  );
}

const CATEGORY_MAP: Record<DashboardTab, string> = {
  overall: "overall",
  paypal: "paypal",
  byBrand: "brands",
  byAccount: "pp_accounts",
};

export default function DisputesMetrics({
  startDate,
  endDate,
  companyId,
  brandId,
  currency,
  activeTab = "overall",
  assignedOnly = false,
  onTabsAvailable,
}: {
  startDate: string,
  endDate: string,
  companyId: number | null,
  brandId?: number | null,
  currency: Currency,
  activeTab?: DashboardTab,
  assignedOnly?: boolean,
  onTabsAvailable?: (tabs: { hasPaypalAccount: boolean; hasMultipleBrands: boolean; hasMultiplePpAccounts: boolean }) => void,
}) {
  const { get, loading, error } = useApi();
  const [{ user }] = useStateValue();
  const [overallStats, setOverallStats] = useState<OverallStatsResponse | null>(null);
  const [paypalStats, setPaypalStats] = useState<PaypalStatsResponse | null>(null);
  const [brandsStats, setBrandsStats] = useState<BrandsStatsResponse | null>(null);
  const [accountsStats, setAccountsStats] = useState<AccountsStatsResponse | null>(null);

  useEffect(() => {
    const params = new URLSearchParams({
      startDate,
      endDate,
      currency,
      category: CATEGORY_MAP[activeTab],
      ...(companyId && { companyId: companyId.toString() }),
      ...(brandId && { brandId: brandId.toString() }),
      ...(assignedOnly && { assignedOnly: 'true' }),
    });

    const fetchStats = async () => {
      try {
        const url = `/api/disputes/stats/v2?${params.toString()}`;
        let response: StatsResponseBase;
        if (activeTab === "overall") {
          const data = await get<OverallStatsResponse>(url);
          setOverallStats(data);
          response = data;
        } else if (activeTab === "paypal") {
          const data = await get<PaypalStatsResponse>(url);
          setPaypalStats(data);
          response = data;
        } else if (activeTab === "byBrand") {
          const data = await get<BrandsStatsResponse>(url);
          setBrandsStats(data);
          response = data;
        } else {
          const data = await get<AccountsStatsResponse>(url);
          setAccountsStats(data);
          response = data;
        }
        onTabsAvailable?.({ hasPaypalAccount: response.hasPaypalAccount, hasMultipleBrands: response.hasMultipleBrands, hasMultiplePpAccounts: response.hasMultiplePpAccounts });
      } catch (err) {
        console.error("Failed to fetch disputes stats:", err);
      }
    };

    fetchStats();
  }, [startDate, endDate, companyId, brandId, currency, activeTab, assignedOnly, get]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-6 lg:grid-cols-5">
        {[...Array(10)].map((_, index) => (
          <div key={index} className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
            <div className="animate-pulse">
              <div className="flex items-center justify-center w-12 h-12 bg-gray-200 rounded-xl dark:bg-gray-700"></div>
              <div className="mt-5">
                <div className="h-4 bg-gray-200 rounded dark:bg-gray-700 w-20"></div>
                <div className="h-6 bg-gray-200 rounded dark:bg-gray-700 w-16 mt-2"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-5 dark:border-red-800 dark:bg-red-900/20 md:p-6">
        <p className="text-red-600 dark:text-red-400">
          Failed to load disputes statistics. Please try again later.
        </p>
      </div>
    );
  }

  // Deep-link into the Disputes list (companyId is preserved by StatCard's CmLink). Each link
  // mirrors the exact filter dimensions the matching KPI counts, so the list count lines up
  // with the card. Every link must pin cmStatuses — either its own value or "all" — or the
  // list's default (open) narrows the drill-down below the KPI it came from.
  const disputesLink = (extra: Record<string, string>) => {
    const p = new URLSearchParams();
    if (startDate && endDate) {
      p.set("range", "custom");
      p.set("start", startDate);
      p.set("end", endDate);
    }
    for (const [k, v] of Object.entries(extra)) p.set(k, v);
    return `/disputes?${p.toString()}`;
  };
  // Snapshot KPIs (open / under-review / inquiries awaiting response) are all-time state-now
  // counts, so their links drop the dashboard date window (range=all clears the list's
  // default 30D filter).
  const allTimeDisputesLink = (extra: Record<string, string>) => {
    const p = new URLSearchParams({ range: "all" });
    for (const [k, v] of Object.entries(extra)) p.set(k, v);
    return `/disputes?${p.toString()}`;
  };
  const isAdmin = hasPermission(user, PERMISSIONS.DISPUTES_ALL_COMPANIES);
  const assignedScope: Record<string, string> = assignedOnly || !isAdmin ? { userIds: "assigned" } : {};
  const snapshotLink = (cmStatuses: string, scope: Record<string, string> = {}) =>
    allTimeDisputesLink({ cmStatuses, ppStatuses: "all", skipRdr: "true", ...scope });
  const inquiriesLink = (scope: Record<string, string> = {}) =>
    allTimeDisputesLink({ ppCaseTypes: "INQUIRY", ppStatuses: "waiting_for_seller_response", cmStatuses: "all", ...scope });
  // Flow KPIs (won / lost / refunded / accepted / resolved) are bound by closed_at, so the
  // link switches the list to the "closed" date field to match.
  const flowLink = (cmStatuses: string, scope: Record<string, string> = {}) =>
    disputesLink({ dateField: "closed", cmStatuses, ppStatuses: "all", skipRdr: "true", ...assignedScope, ...scope });
  // Chargebacks Received is bound by received (created) date across all statuses, RDR excluded.
  // Reuse the disputes list's existing Case Type = Chargeback filter so the drill-down lands with
  // a visible filter chip applied.
  const chargebackLink = (scope: Record<string, string> = {}) =>
    disputesLink({ ppCaseTypes: "CHARGEBACK", ppStatuses: "all", cmStatuses: "all", skipRdr: "true", ...assignedScope, ...scope });
  const paypalLinks = { flow: flowLink, snapshot: snapshotLink, inquiries: inquiriesLink, assignedScope };

  return (
    <>
      {/* Overall Tab - General Metrics */}
      {activeTab === "overall" && overallStats && (
        <>
          {(() => {
            const cp = overallStats.currentPeriod;
            const pp = overallStats.previousPeriod;
            const hasOpen = cp.openDisputes !== undefined;
            const hasInquiries = overallStats.needResponseInquiries !== undefined;
            // Hero pulls Win Rate / Amount Recovered / Under Review / Won out of the grid.
            // Grid cells = Outcomes card (spans 2) + 4 single metrics + optional Open/Awaiting
            // → even 4×4 when both optionals are present.
            const colsClass = "lg:grid-cols-4";
            return (
              <>
                {/* Hero — "are we winning, and what needs attention?" */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-6">
                  <DashboardItem
                    size="hero"
                    label="Win Rate"
                    value={cp.winRate}
                    previousValue={pp.winRate}
                    valueSymbol="%"
                    badgeDeltaSymbol="%"
                    tone={cp.resolvedDisputes > 0 ? (cp.winRate >= 65 ? "good" : cp.winRate >= 45 ? "warn" : "bad") : undefined}
                  />
                  <DashboardItem
                    size="hero"
                    label="Amount Recovered"
                    value={cp.recoveredAmount}
                    previousValue={pp.recoveredAmount}
                    isCurrency={true}
                    currency={currency}
                    href={flowLink("won")}
                  />
                  <DashboardItem
                    size="hero"
                    label="Under Review"
                    value={cp.underReviewDisputes}
                    href={snapshotLink("under_review", assignedScope)}
                  />
                  <DashboardItem
                    size="hero"
                    label="Disputes Won"
                    value={cp.wonDisputes}
                    previousValue={pp.wonDisputes}
                    href={flowLink("won")}
                  />
                </div>

                <h3 className="mt-8 mb-4 text-lg font-semibold text-gray-800 dark:text-white">
                  Disputes
                </h3>
                <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-6 ${colsClass}`}>
                  <div className="sm:col-span-2">
                    <OutcomesBreakdown
                      won={cp.wonDisputes}
                      lost={cp.lostDisputes}
                      accepted={cp.acceptedDisputes}
                      refunded={cp.refundedDisputes}
                    />
                  </div>
                  <DashboardItem
                    icon={<AlertIcon className="text-orange-600 size-6 dark:text-orange-400" />}
                    iconBg="bg-orange-100 dark:bg-orange-900/30"
                    label="Chargebacks Received"
                    value={cp.chargebacksReceived}
                    previousValue={pp.chargebacksReceived}
                    href={chargebackLink()}
                  />
                  <DashboardItem
                    icon={<AlertIcon className="text-orange-600 size-6 dark:text-orange-400" />}
                    iconBg="bg-orange-100 dark:bg-orange-900/30"
                    label="Disputes Resolved"
                    value={cp.resolvedDisputes}
                    previousValue={pp.resolvedDisputes}
                    tooltip="Won + Lost + Refunded disputes (excludes Accepted)"
                    href={flowLink("won,lost,refunded")}
                  />
                  <DashboardItem
                    icon={<DollarLineIcon className="text-blue-600 size-6 dark:text-blue-400" />}
                    iconBg="bg-blue-100 dark:bg-blue-900/30"
                    label="Inquiries Refunded"
                    value={cp.refundedDisputes}
                    previousValue={pp.refundedDisputes}
                    href={flowLink("refunded")}
                  />
                  <DashboardItem
                    icon={<DollarLineIcon className="text-blue-600 size-6 dark:text-blue-400" />}
                    iconBg="bg-blue-100 dark:bg-blue-900/30"
                    label="Amount Refunded"
                    value={cp.refundedAmount}
                    previousValue={pp.refundedAmount}
                    isCurrency={true}
                    currency={currency}
                    href={flowLink("refunded")}
                  />
                  {hasOpen && (
                    <DashboardItem
                      icon={<AlertIcon className="text-orange-600 size-6 dark:text-orange-400" />}
                      iconBg="bg-orange-100 dark:bg-orange-900/30"
                      label="Open Disputes"
                      value={cp.openDisputes!}
                      href={snapshotLink("open")}
                    />
                  )}
                  {hasInquiries && (
                    <DashboardItem
                      icon={<TimeIcon className="text-yellow-600 size-6 dark:text-yellow-400" />}
                      iconBg="bg-yellow-100 dark:bg-yellow-900/30"
                      label="Inquiries Awaiting Response"
                      value={overallStats.needResponseInquiries!}
                      href={inquiriesLink({ ppTypes: "paypal" })}
                    />
                  )}
                </div>
              </>
            );
          })()}
        </>
      )}

      {/* PayPal Tab - PayPal Specific Metrics */}
      {activeTab === "paypal" && paypalStats && (
        <>
          {isStatsNotEmpty(paypalStats.paypalDispute) && paypalStats.paypalDispute && (
            paypalDashboardRow("PayPal Inquiries", paypalStats.paypalDispute, currency, paypalLinks, "Dispute", true, paypalStats.needResponseInquiries)
          )}

          {isStatsNotEmpty(paypalStats.paypalClaim) && paypalStats.paypalClaim && (
            paypalDashboardRow("PayPal Claims", paypalStats.paypalClaim, currency, paypalLinks, "Claim")
          )}

          {isStatsNotEmpty(paypalStats.paypalUnauthorizedClaim) && paypalStats.paypalUnauthorizedClaim && (
            paypalDashboardRow("PayPal Unauthorized Claims", paypalStats.paypalUnauthorizedClaim, currency, paypalLinks, "Unauthorized Claim")
          )}

          {isStatsNotEmpty(paypalStats.paypalBankReturn) && paypalStats.paypalBankReturn && (
            paypalDashboardRow("PayPal Bank Returns", paypalStats.paypalBankReturn, currency, paypalLinks, "Bank Return")
          )}

          {isStatsNotEmpty(paypalStats.paypalCardChargeback) && paypalStats.paypalCardChargeback && (
            paypalDashboardRow("PayPal Card Chargebacks", paypalStats.paypalCardChargeback, currency, paypalLinks, "Card Chargeback")
          )}

          {isStatsNotEmpty(paypalStats.paypalPreChargebackAlert) && paypalStats.paypalPreChargebackAlert && (
            paypalDashboardRow("PayPal Pre-Chargeback Alerts", paypalStats.paypalPreChargebackAlert, currency, paypalLinks, "Pre-chargeback Alert")
          )}

          {isStatsNotEmpty(paypalStats.paypalUnknown) && paypalStats.paypalUnknown && (
            paypalDashboardRow("PayPal Unknown", paypalStats.paypalUnknown, currency, paypalLinks, "Unknown")
          )}
        </>
      )}

      {/* By Brand Tab */}
      {activeTab === "byBrand" && brandsStats?.currentPeriodByBrand && (
        <>
          {brandsStats.currentPeriodByBrand.filter(isStatsNotEmpty).map((brand) => (
            <div key={brand.brandId}>
              <h3 className="mt-8 mb-4 text-lg font-semibold text-gray-800 dark:text-white">
                {brand.brandName}
              </h3>
              {(() => {
                const hasOpen = brand.openDisputes !== undefined;
                const itemCount = 10 + (hasOpen ? 1 : 0);
                const lgCols = itemCount <= 8 ? "lg:grid-cols-4" : itemCount <= 10 ? "lg:grid-cols-5" : "lg:grid-cols-6";
                const brandScope = { brandId: String(brand.brandId) };
                return (
                  <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-6 ${lgCols}`}>
                    <DashboardItem
                      icon={<AlertIcon className="text-orange-600 size-6 dark:text-orange-400" />}
                      iconBg="bg-orange-100 dark:bg-orange-900/30"
                      label="Chargebacks Received"
                      value={brand.chargebacksReceived}
                      href={chargebackLink(brandScope)}
                    />
                    <DashboardItem
                      icon={<AlertIcon className="text-orange-600 size-6 dark:text-orange-400" />}
                      iconBg="bg-orange-100 dark:bg-orange-900/30"
                      label="Disputes Resolved"
                      value={brand.resolvedDisputes}
                      tooltip="Won + Lost + Refunded disputes (excludes Accepted)"
                      href={flowLink("won,lost,refunded", brandScope)}
                    />
                    <DashboardItem
                      icon={<CheckCircleIcon className="text-green-600 size-6 dark:text-green-400" />}
                      iconBg="bg-green-100 dark:bg-green-900/30"
                      label="Disputes Won"
                      value={brand.wonDisputes}
                      href={flowLink("won", brandScope)}
                    />
                    <DashboardItem
                      icon={<DollarLineIcon className="text-blue-600 size-6 dark:text-blue-400" />}
                      iconBg="bg-blue-100 dark:bg-blue-900/30"
                      label="Disputes Lost"
                      value={brand.lostDisputes}
                      href={flowLink("lost", brandScope)}
                    />
                    <DashboardItem
                      icon={<CheckCircleIcon className="text-green-600 size-6 dark:text-green-400" />}
                      iconBg="bg-green-100 dark:bg-green-900/30"
                      label="Disputes Accepted"
                      value={brand.acceptedDisputes}
                      href={flowLink("accepted", brandScope)}
                    />
                    <DashboardItem
                      icon={<CheckCircleIcon className="text-green-600 size-6 dark:text-green-400" />}
                      iconBg="bg-green-100 dark:bg-green-900/30"
                      label="Win Rate"
                      value={brand.winRate}
                      valueSymbol="%"
                    />
                    <DashboardItem
                      icon={<DollarLineIcon className="text-blue-600 size-6 dark:text-blue-400" />}
                      iconBg="bg-blue-100 dark:bg-blue-900/30"
                      label="Amount Recovered"
                      value={brand.recoveredAmount}
                      isCurrency={true}
                      currency={currency}
                      href={flowLink("won", brandScope)}
                    />
                    <DashboardItem
                      icon={<DollarLineIcon className="text-blue-600 size-6 dark:text-blue-400" />}
                      iconBg="bg-blue-100 dark:bg-blue-900/30"
                      label="Inquiries Refunded"
                      value={brand.refundedDisputes}
                      href={flowLink("refunded", brandScope)}
                    />
                    <DashboardItem
                      icon={<DollarLineIcon className="text-blue-600 size-6 dark:text-blue-400" />}
                      iconBg="bg-blue-100 dark:bg-blue-900/30"
                      label="Amount Refunded"
                      value={brand.refundedAmount}
                      isCurrency={true}
                      currency={currency}
                      href={flowLink("refunded", brandScope)}
                    />
                    <DashboardItem
                      icon={<TimeIcon className="text-yellow-600 size-6 dark:text-yellow-400" />}
                      iconBg="bg-yellow-100 dark:bg-yellow-900/30"
                      label="Under Review"
                      value={brand.underReviewDisputes}
                      href={snapshotLink("under_review", { ...brandScope, ...assignedScope })}
                    />
                    {hasOpen && (
                      <DashboardItem
                        icon={<AlertIcon className="text-orange-600 size-6 dark:text-orange-400" />}
                        iconBg="bg-orange-100 dark:bg-orange-900/30"
                        label="Open Disputes"
                        value={brand.openDisputes!}
                        href={snapshotLink("open", brandScope)}
                      />
                    )}
                  </div>
                );
              })()}
            </div>
          ))}
        </>
      )}

      {/* By Account Tab */}
      {activeTab === "byAccount" && accountsStats?.currentPeriodByAccount && (
        <>
          {accountsStats.currentPeriodByAccount.filter(isStatsNotEmpty).map((account) => (
            <div key={account.accountId}>
              <h3 className="mt-8 mb-4 text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
                <PpLogo ppType={account.accountType} />
                {account.accountName}
              </h3>
              {(() => {
                const hasOpen = account.openDisputes !== undefined;
                const hasInquiries = account.needResponseInquiries !== undefined;
                const itemCount = 10 + (hasOpen ? 1 : 0) + (hasInquiries ? 1 : 0);
                const lgCols = itemCount <= 8 ? "lg:grid-cols-4" : itemCount <= 10 ? "lg:grid-cols-5" : "lg:grid-cols-6";
                // Scope the deep-links to this single pp-account (same key the list's ppAccounts filter uses).
                const acctKey = `${account.accountType}:${account.accountId}`;
                const acctScope = { ppAccounts: acctKey };
                return (
                  <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-6 ${lgCols}`}>
                    <DashboardItem
                      icon={<AlertIcon className="text-orange-600 size-6 dark:text-orange-400" />}
                      iconBg="bg-orange-100 dark:bg-orange-900/30"
                      label="Chargebacks Received"
                      value={account.chargebacksReceived}
                      href={chargebackLink(acctScope)}
                    />
                    <DashboardItem
                      icon={<AlertIcon className="text-orange-600 size-6 dark:text-orange-400" />}
                      iconBg="bg-orange-100 dark:bg-orange-900/30"
                      label="Disputes Resolved"
                      value={account.resolvedDisputes}
                      tooltip="Won + Lost + Refunded disputes (excludes Accepted)"
                      href={flowLink("won,lost,refunded", acctScope)}
                    />
                    <DashboardItem
                      icon={<CheckCircleIcon className="text-green-600 size-6 dark:text-green-400" />}
                      iconBg="bg-green-100 dark:bg-green-900/30"
                      label="Disputes Won"
                      value={account.wonDisputes}
                      href={flowLink("won", acctScope)}
                    />
                    <DashboardItem
                      icon={<DollarLineIcon className="text-blue-600 size-6 dark:text-blue-400" />}
                      iconBg="bg-blue-100 dark:bg-blue-900/30"
                      label="Disputes Lost"
                      value={account.lostDisputes}
                      href={flowLink("lost", acctScope)}
                    />
                    <DashboardItem
                      icon={<CheckCircleIcon className="text-green-600 size-6 dark:text-green-400" />}
                      iconBg="bg-green-100 dark:bg-green-900/30"
                      label="Disputes Accepted"
                      value={account.acceptedDisputes}
                      href={flowLink("accepted", acctScope)}
                    />
                    <DashboardItem
                      icon={<CheckCircleIcon className="text-green-600 size-6 dark:text-green-400" />}
                      iconBg="bg-green-100 dark:bg-green-900/30"
                      label="Win Rate"
                      value={account.winRate}
                      valueSymbol="%"
                    />
                    <DashboardItem
                      icon={<DollarLineIcon className="text-blue-600 size-6 dark:text-blue-400" />}
                      iconBg="bg-blue-100 dark:bg-blue-900/30"
                      label="Amount Recovered"
                      value={account.recoveredAmount}
                      isCurrency={true}
                      currency={currency}
                      href={flowLink("won", acctScope)}
                    />
                    <DashboardItem
                      icon={<DollarLineIcon className="text-blue-600 size-6 dark:text-blue-400" />}
                      iconBg="bg-blue-100 dark:bg-blue-900/30"
                      label="Inquiries Refunded"
                      value={account.refundedDisputes}
                      href={flowLink("refunded", acctScope)}
                    />
                    <DashboardItem
                      icon={<DollarLineIcon className="text-blue-600 size-6 dark:text-blue-400" />}
                      iconBg="bg-blue-100 dark:bg-blue-900/30"
                      label="Amount Refunded"
                      value={account.refundedAmount}
                      isCurrency={true}
                      currency={currency}
                      href={flowLink("refunded", acctScope)}
                    />
                    <DashboardItem
                      icon={<TimeIcon className="text-yellow-600 size-6 dark:text-yellow-400" />}
                      iconBg="bg-yellow-100 dark:bg-yellow-900/30"
                      label="Under Review"
                      value={account.underReviewDisputes}
                      href={snapshotLink("under_review", { ...acctScope, ...assignedScope })}
                    />
                    {hasOpen && (
                      <DashboardItem
                        icon={<AlertIcon className="text-orange-600 size-6 dark:text-orange-400" />}
                        iconBg="bg-orange-100 dark:bg-orange-900/30"
                        label="Open Disputes"
                        value={account.openDisputes!}
                        href={snapshotLink("open", acctScope)}
                      />
                    )}
                    {hasInquiries && (
                      <DashboardItem
                        icon={<TimeIcon className="text-yellow-600 size-6 dark:text-yellow-400" />}
                        iconBg="bg-yellow-100 dark:bg-yellow-900/30"
                        label="Inquiries Awaiting Response"
                        value={account.needResponseInquiries!}
                        href={inquiriesLink({ ppAccounts: acctKey })}
                      />
                    )}
                  </div>
                );
              })()}
            </div>
          ))}
        </>
      )}
    </>
  );
}
